import { useEffect, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Play, TrendingUp, TrendingDown, Layers } from "lucide-react";
import { toast } from "sonner";

const UNIS = ["NIFTY_50", "NIFTY_100", "NIFTY_200", "NIFTY_500"];

function Stat({ label, value, color }) {
  return (
    <div className="bg-elevated border border-edge rounded-sm p-2.5">
      <div className="text-[9px] uppercase tracking-wider text-muted2">{label}</div>
      <div className="mono text-sm font-bold mt-0.5" style={{ color: color || "#fff" }}>{value}</div>
    </div>
  );
}

export default function Backtest() {
  const [p, setP] = useState({ universe: "NIFTY_50", days: 30, target_pct: 0.5, stop_pct: 2.0, momentum_threshold: 1.0, notional_per_stock: 100000, data_source: "SYNTHETIC", max_symbols: 50, entry_mode: "MOMENTUM", require_above_vwap: false, min_rel_volume: 0, max_concurrent: 10, max_trades_day: 50 });
  const [strategy, setStrategy] = useState("INTRADAY");
  const [running, setRunning] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [swinging, setSwinging] = useState(false);
  const [res, setRes] = useState(null);
  const [sweep, setSweep] = useState(null);
  const [swing, setSwing] = useState(null);

  useEffect(() => { api.getConfig().then((c) => setP((x) => ({ ...x, target_pct: c.target_pct, stop_pct: c.stop_pct, momentum_threshold: c.momentum_threshold, notional_per_stock: c.notional_per_stock, universe: c.universe }))); }, []);

  const run = async () => {
    setRunning(true); setSweep(null);
    try {
      const r = await api.backtest(p);
      setRes(r);
      toast.success("Backtest complete", { description: `${r.total_trades} trades · WR ${r.win_rate}%` });
    } catch (e) {
      toast.error("Backtest failed", { description: e?.response?.data?.detail });
    } finally {
      setRunning(false);
    }
  };

  const runSweep = async () => {
    setSweeping(true); setRes(null);
    try {
      const r = await api.backtestSweep(p);
      setSweep(r);
      toast.success("Sweep complete", { description: `${r.combos.length} combos on ${r.days} days` });
    } catch (e) {
      toast.error("Sweep failed", { description: e?.response?.data?.detail });
    } finally {
      setSweeping(false);
    }
  };

  const isReal = p.data_source === "REAL";
  const isRev = p.entry_mode === "REVERSION";
  const clears = res?.clears_breakeven;
  const busy = running || sweeping || swinging;

  const runSwing = async () => {
    setSwinging(true); setSwing(null);
    try {
      const body = { universe: p.universe, days: p.days, target_pct: p.target_pct, stop_pct: p.stop_pct, momentum_threshold: p.momentum_threshold, max_concurrent: p.max_concurrent, max_trades_day: p.max_trades_day, notional_per_stock: p.notional_per_stock, data_source: p.data_source, max_symbols: p.max_symbols };
      const r = await api.backtestSwing(body);
      setSwing(r);
      toast.success("Swing backtest complete", { description: `${r.closed_trades} closed · WR ${r.win_rate_closed}%` });
    } catch (e) {
      toast.error("Swing backtest failed", { description: e?.response?.data?.detail });
    } finally {
      setSwinging(false);
    }
  };

  return (
    <div className="pb-40">
      <div className="glass sticky top-0 z-30 border-b border-edge px-4 py-3">
        <h1 className="text-xl font-bold tracking-tight">Backtester</h1>
        <p className="text-[11px] text-muted2">Historical replay · cost + slippage modeled</p>
      </div>

      <div className="p-4 space-y-4">
        {/* Strategy selector */}
        <div className="grid grid-cols-2 gap-1.5">
          <button onClick={() => setStrategy("INTRADAY")} data-testid="strat-intraday"
            className={`py-2.5 rounded-sm text-xs font-bold border ${strategy === "INTRADAY" ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"}`}>
            INTRADAY SCALP
          </button>
          <button onClick={() => setStrategy("SWING") || setP((x) => ({ ...x, universe: "NIFTY_500", notional_per_stock: 100000, target_pct: 2.0, stop_pct: 0, momentum_threshold: 1.0, max_concurrent: 10, max_trades_day: 50 }))} data-testid="strat-swing"
            className={`py-2.5 rounded-sm text-xs font-bold border ${strategy === "SWING" ? "bg-pos/15 border-pos text-pos" : "bg-elevated border-edge text-muted2"}`}>
            SWING (multi-day)
          </button>
        </div>

        {strategy === "INTRADAY" && (
        <>
        <div className="bg-surface border border-edge rounded-sm p-3 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted2 mb-1.5">Data Source</div>
            <div className="grid grid-cols-2 gap-1.5">
              <button onClick={() => setP((x) => ({ ...x, data_source: "SYNTHETIC" }))} data-testid="bt-src-synthetic"
                className={`py-2 rounded-sm text-[11px] font-bold border ${!isReal ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"}`}>
                SYNTHETIC
              </button>
              <button onClick={() => setP((x) => ({ ...x, data_source: "REAL" }))} data-testid="bt-src-real"
                className={`py-2 rounded-sm text-[11px] font-bold border ${isReal ? "bg-pos/15 border-pos text-pos" : "bg-elevated border-edge text-muted2"}`}>
                REAL NSE DATA
              </button>
            </div>
            {isReal && (
              <p className="mt-1.5 text-[10px] text-warn/80 leading-snug" data-testid="bt-real-note">
                Uses live Kite 1-min history. Needs Kite login + historical-data subscription. Samples the first {p.max_symbols} symbols · may take up to a minute.
              </p>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted2 mb-1.5">Universe</div>
            <div className="grid grid-cols-4 gap-1.5">
              {UNIS.map((u) => (
                <button key={u} onClick={() => setP((x) => ({ ...x, universe: u }))} data-testid={`bt-uni-${u}`}
                  className={`py-2 rounded-sm text-[11px] font-bold border ${p.universe === u ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"}`}>
                  {u.replace("NIFTY_", "N")}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Lbl t="Days">{ni(p.days, (v) => setP((x) => ({ ...x, days: v })), "bt-days")}</Lbl>
            <Lbl t="Target %">{ni(p.target_pct, (v) => setP((x) => ({ ...x, target_pct: v })), "bt-target", 0.1)}</Lbl>
            <Lbl t="Stop %">{ni(p.stop_pct, (v) => setP((x) => ({ ...x, stop_pct: v })), "bt-stop", 0.1)}</Lbl>
            <Lbl t="Mom %">{ni(p.momentum_threshold, (v) => setP((x) => ({ ...x, momentum_threshold: v })), "bt-mom", 0.1)}</Lbl>
            {isReal
              ? <Lbl t="Symbols">{ni(p.max_symbols, (v) => setP((x) => ({ ...x, max_symbols: v })), "bt-maxsym")}</Lbl>
              : null}
            <Lbl t="Notional ₹" span2={!isReal}>{ni(p.notional_per_stock, (v) => setP((x) => ({ ...x, notional_per_stock: v })), "bt-notional")}</Lbl>
          </div>

          {/* Entry-quality filters */}
          <div className="border-t border-edge pt-3 space-y-2.5">
            <div className="text-[10px] uppercase tracking-wider text-muted2">Entry Quality (find an edge)</div>
            <div className="grid grid-cols-2 gap-1.5">
              <button onClick={() => setP((x) => ({ ...x, entry_mode: "MOMENTUM" }))} data-testid="bt-mode-momentum"
                className={`py-2 rounded-sm text-[10px] font-bold border ${!isRev ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"}`}>
                MOMENTUM (buy strength)
              </button>
              <button onClick={() => setP((x) => ({ ...x, entry_mode: "REVERSION" }))} data-testid="bt-mode-reversion"
                className={`py-2 rounded-sm text-[10px] font-bold border ${isRev ? "bg-warn/15 border-warn text-warn" : "bg-elevated border-edge text-muted2"}`}>
                REVERSION (fade dips)
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 items-end">
              <button onClick={() => setP((x) => ({ ...x, require_above_vwap: !x.require_above_vwap }))} data-testid="bt-vwap-toggle"
                className={`py-2 rounded-sm text-[10px] font-bold border ${p.require_above_vwap ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"}`}>
                {isRev ? "Only below VWAP" : "Only above VWAP"} {p.require_above_vwap ? "ON" : "OFF"}
              </button>
              <Lbl t="Min Rel-Volume (0=off)">{ni(p.min_rel_volume, (v) => setP((x) => ({ ...x, min_rel_volume: v })), "bt-relvol", 0.1)}</Lbl>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button onClick={run} disabled={busy} className="flex items-center justify-center gap-2 py-3 rounded-sm bg-brand text-white font-bold tracking-wider active:scale-[0.99] transition-transform duration-150 disabled:opacity-50" data-testid="run-backtest-btn">
              <Play size={15} strokeWidth={2.5} /> {running ? "RUNNING…" : "RUN"}
            </button>
            <button onClick={runSweep} disabled={busy} className="flex items-center justify-center gap-2 py-3 rounded-sm bg-elevated border border-brand/50 text-brand font-bold tracking-wider active:scale-[0.99] transition-transform duration-150 disabled:opacity-50" data-testid="run-sweep-btn">
              <Layers size={15} strokeWidth={2.5} /> {sweeping ? "SWEEPING…" : "PARAM SWEEP"}
            </button>
          </div>
          <p className="text-[10px] text-muted2 text-center -mt-1">Sweep tests 16 Target×Stop combos on one data pull · ranks by net P&L</p>
        </div>

        {sweep && (
          <div className="bg-surface border border-edge rounded-sm p-3" data-testid="sweep-results">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-muted2">Parameter Sweep — best first</div>
              <span className="mono text-[9px] text-muted2">{sweep.data_source === "REAL_KITE" ? `REAL · ${sweep.symbols_with_data}/${sweep.symbols_requested} sym · ${sweep.days}d` : `SYNTHETIC · ${sweep.days}d`}</span>
            </div>
            <div className="grid grid-cols-6 gap-1 text-[9px] uppercase tracking-wider text-muted2 pb-1 border-b border-edge">
              <span>Tgt</span><span>Stop</span><span>Trades</span><span>WR%</span><span>BE%</span><span className="text-right">Net ₹</span>
            </div>
            {sweep.combos.map((c, i) => (
              <div key={i} className={`grid grid-cols-6 gap-1 py-1.5 text-[11px] mono border-b border-edge/40 ${c.clears_breakeven ? "text-pos" : "text-white"}`} data-testid={`sweep-row-${i}`}>
                <span>{c.target_pct}</span><span>{c.stop_pct}</span><span>{c.total_trades}</span>
                <span>{c.win_rate}</span><span className="text-muted2">{c.breakeven_winrate}</span>
                <span className="text-right font-bold" style={{ color: c.net_pnl >= 0 ? "#00E676" : "#FF3B30" }}>{fmtINR(c.net_pnl)}</span>
              </div>
            ))}
            <p className="text-[9px] text-muted2 mt-2">Green rows clear breakeven (WR ≥ BE). Tap a good combo's Target/Stop into the fields above and Run for full detail.</p>
          </div>
        )}

        {res && (
          <>
            {/* Breakeven banner */}
            <div className={`rounded-sm p-4 border ${clears ? "bg-pos/10 border-pos/40" : "bg-neg/10 border-neg/40"}`} data-testid="breakeven-banner">
              <div className="flex items-center gap-2">
                {clears ? <TrendingUp className="text-pos" size={18} /> : <TrendingDown className="text-neg" size={18} />}
                <span className={`text-sm font-bold tracking-wide ${clears ? "text-pos" : "text-neg"}`}>
                  {clears ? "CLEARS BREAKEVEN" : "BELOW BREAKEVEN"}
                </span>
              </div>
              <div className="flex items-end gap-6 mt-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted2">Win Rate</div>
                  <div className="mono text-3xl font-extrabold" style={{ color: clears ? "#00E676" : "#FF3B30" }}>{res.win_rate}%</div>
                </div>
                <div className="pb-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted2">Breakeven Needed</div>
                  <div className="mono text-lg font-bold text-white">{res.breakeven_winrate}%</div>
                </div>
                <div className="pb-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted2">R:R</div>
                  <div className="mono text-lg font-bold text-white">1:{res.rr_ratio}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Stat label="Trades" value={res.total_trades} />
              <Stat label="Wins" value={res.wins} color="#00E676" />
              <Stat label="Losses" value={res.losses} color="#FF3B30" />
              <Stat label="Net P&L" value={fmtINR(res.net_pnl)} color={res.net_pnl >= 0 ? "#00E676" : "#FF3B30"} />
              <Stat label="Avg R" value={res.avg_r_multiple} />
              <Stat label="Max DD" value={fmtINR(res.max_drawdown)} color="#FF3B30" />
              <Stat label="Gross P&L" value={fmtINR(res.gross_pnl)} />
              <Stat label="Total Costs" value={fmtINR(res.total_costs)} color="#FFB300" />
              <Stat label="Cost Impact" value={`${res.cost_impact_pct}%`} color="#FFB300" />
            </div>

            <div className="bg-surface border border-edge rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted2 mb-2">Exit Breakdown</div>
              <div className="flex gap-4">
                {Object.entries(res.exit_breakdown || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${k === "TARGET" ? "bg-pos" : k === "STOP" ? "bg-neg" : "bg-warn"}`} />
                    <span className="text-xs text-white">{k}</span>
                    <span className="mono text-xs text-muted2">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-center" data-testid="bt-source-note">
              {res.data_source === "REAL_KITE"
                ? <span className="text-pos/80">✓ Real NSE 1-min data · {res.symbols_with_data}/{res.symbols_requested} symbols · {res.days} trading days</span>
                : <span className="text-warn/70">Synthetic bars — switch to REAL NSE DATA for a live-history backtest</span>}
            </p>
          </>
        )}
        </>
        )}

        {strategy === "SWING" && (
        <>
          <div className="bg-surface border border-edge rounded-sm p-3 space-y-3">
            <p className="text-[10px] text-warn/80 leading-snug" data-testid="swing-note">
              Enters on a +{p.momentum_threshold}% intraday move (top {p.max_concurrent} by volume, max {p.max_trades_day}/day), holds across days until +{p.target_pct}% profit{Number(p.stop_pct) > 0 ? ` or −${p.stop_pct}% stop` : " — NO stop-loss (held until target)"}. Delivery (CNC) cost model. Watch "still open / max drawdown" — that's the hidden risk.
            </p>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted2 mb-1.5">Data Source</div>
              <div className="grid grid-cols-2 gap-1.5">
                <button onClick={() => setP((x) => ({ ...x, data_source: "SYNTHETIC" }))} data-testid="sw-src-synthetic"
                  className={`py-2 rounded-sm text-[11px] font-bold border ${!isReal ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"}`}>SYNTHETIC</button>
                <button onClick={() => setP((x) => ({ ...x, data_source: "REAL" }))} data-testid="sw-src-real"
                  className={`py-2 rounded-sm text-[11px] font-bold border ${isReal ? "bg-pos/15 border-pos text-pos" : "bg-elevated border-edge text-muted2"}`}>REAL NSE DATA</button>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted2 mb-1.5">Universe</div>
              <div className="grid grid-cols-4 gap-1.5">
                {UNIS.map((u) => (
                  <button key={u} onClick={() => setP((x) => ({ ...x, universe: u }))} data-testid={`sw-uni-${u}`}
                    className={`py-2 rounded-sm text-[11px] font-bold border ${p.universe === u ? "bg-brand/15 border-brand text-brand" : "bg-elevated border-edge text-muted2"}`}>
                    {u.replace("NIFTY_", "N")}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Lbl t="Days">{ni(p.days, (v) => setP((x) => ({ ...x, days: v })), "sw-days")}</Lbl>
              <Lbl t="Entry Move %">{ni(p.momentum_threshold, (v) => setP((x) => ({ ...x, momentum_threshold: v })), "sw-mom", 0.1)}</Lbl>
              <Lbl t="Target %">{ni(p.target_pct, (v) => setP((x) => ({ ...x, target_pct: v })), "sw-target", 0.1)}</Lbl>
              <Lbl t="Stop %">{ni(p.stop_pct, (v) => setP((x) => ({ ...x, stop_pct: v })), "sw-stop", 0.1)}</Lbl>
              <Lbl t="Max Positions">{ni(p.max_concurrent, (v) => setP((x) => ({ ...x, max_concurrent: v })), "sw-maxpos")}</Lbl>
              <Lbl t="Max Trades/Day">{ni(p.max_trades_day, (v) => setP((x) => ({ ...x, max_trades_day: v })), "sw-maxtrades")}</Lbl>
              {isReal ? <Lbl t="Symbols">{ni(p.max_symbols, (v) => setP((x) => ({ ...x, max_symbols: v })), "sw-maxsym")}</Lbl> : null}
              <Lbl t="Notional ₹" span2={!isReal}>{ni(p.notional_per_stock, (v) => setP((x) => ({ ...x, notional_per_stock: v })), "sw-notional")}</Lbl>
            </div>
            <button onClick={runSwing} disabled={busy} className="w-full flex items-center justify-center gap-2 py-3 rounded-sm bg-pos text-black font-bold tracking-wider active:scale-[0.99] transition-transform duration-150 disabled:opacity-50" data-testid="run-swing-btn">
              <Play size={15} strokeWidth={2.5} /> {swinging ? "RUNNING SWING…" : "RUN SWING BACKTEST"}
            </button>
            {isReal && <p className="text-[10px] text-muted2 text-center">Uses daily history · needs Kite login · ~{Math.ceil(p.max_symbols / 2)}s</p>}
          </div>

          {swing && (
            <div className="space-y-3" data-testid="swing-results">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="Closed Win%" value={`${swing.win_rate_closed}%`} color="#00E676" />
                <Stat label="Closed Trades" value={swing.closed_trades} />
                <Stat label="Avg Hold (d)" value={swing.avg_hold_days} />
                <Stat label="Realized P&L" value={fmtINR(swing.realized_pnl)} color={swing.realized_pnl >= 0 ? "#00E676" : "#FF3B30"} />
                <Stat label="Still Open" value={swing.open_positions} color="#FFB300" />
                <Stat label="Max Hold (d)" value={swing.max_hold_days} />
                <Stat label="Unrealized" value={fmtINR(swing.unrealized_pnl)} color={swing.unrealized_pnl >= 0 ? "#00E676" : "#FF3B30"} />
                <Stat label="NET P&L" value={fmtINR(swing.net_pnl)} color={swing.net_pnl >= 0 ? "#00E676" : "#FF3B30"} />
                <Stat label="Max DD" value={fmtINR(swing.max_drawdown)} color="#FF3B30" />
              </div>
              <div className="bg-surface border border-edge rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted2 mb-1">Total Costs</div>
                <div className="mono text-sm font-bold text-warn">{fmtINR(swing.total_costs)}</div>
              </div>
              {swing.worst_open?.length > 0 && (
                <div className="bg-surface border border-neg/40 rounded-sm p-3" data-testid="swing-stuck">
                  <div className="text-[10px] uppercase tracking-wider text-neg mb-2">⚠ Positions still held at end (the hidden risk)</div>
                  {swing.worst_open.map((o, i) => (
                    <div key={i} className="flex justify-between py-1 text-[11px] mono border-b border-edge/40">
                      <span className="text-white">{o.symbol}</span>
                      <span className="text-muted2">held {o.hold_days}d</span>
                      <span style={{ color: o.drawdown_pct >= 0 ? "#00E676" : "#FF3B30" }}>{o.drawdown_pct}%</span>
                      <span style={{ color: o.unreal >= 0 ? "#00E676" : "#FF3B30" }}>{fmtINR(o.unreal)}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-center" data-testid="swing-source-note">
                {swing.data_source === "REAL_KITE"
                  ? <span className="text-pos/80">✓ Real NSE daily data · {swing.symbols_with_data}/{swing.symbols_requested} symbols · {swing.days} days</span>
                  : <span className="text-warn/70">Synthetic (upward-drift) bars — switch to REAL NSE DATA for the truth</span>}
              </p>
            </div>
          )}
        </>
        )}
      </div>
    </div>
  );
}

function Lbl({ t, span2, children }) {
  return (
    <div className={span2 ? "col-span-2" : ""}>
      <div className="text-[9px] uppercase tracking-wider text-muted2 mb-1">{t}</div>
      {children}
    </div>
  );
}
function ni(value, onChange, testid, step) {
  return <Input type="number" step={step} value={value} onChange={(e) => onChange(+e.target.value)} data-testid={testid} className="bg-elevated border-edge rounded-sm mono text-sm h-9" />;
}
